<template>
  <div class="flex flex-col h-full bg-cream select-none">

    <!-- ═══ Таб-бар: fixed 72px, blur ═══ -->
    <div class="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 flex items-end"
         style="height: calc(72px + var(--safe-top)); background: rgba(235, 225, 210, 0.6); -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);">
      <button
        v-for="(tab, idx) in tabs"
        :key="tab.key"
        :data-guide="'cook-tab-' + tab.key"
        @click="activeTab = tab.key"
        class="flex-1 flex flex-col items-center justify-end transition-colors relative"
        :class="{
          'tab-active': activeTab === tab.key,
          'tab-first': idx === 0,
          'tab-last': idx === tabs.length - 1,
        }"
        :style="[
          activeTab === tab.key
            ? 'background: rgba(250, 243, 232, 0.75); z-index: 2;'
            : 'background: transparent; z-index: 1;',
          'height: 72px; padding-bottom: 12px;',
          idx === 0 ? 'border-radius: 0 14px 0 0;'
            : idx === tabs.length - 1 ? 'border-radius: 14px 0 0 0;'
            : 'border-radius: 14px 14px 0 0;'
        ]"
      >
        <div class="flex-1 flex items-end pb-1" :style="{ opacity: activeTab === tab.key ? 1 : 0.6, color: '#41291A' }" v-html="tab.svg" />
        <span style="font-family: Inter, sans-serif; font-weight: 400; font-size: var(--fs-12); line-height: 1;"
              :style="{ color: activeTab === tab.key ? '#41291A' : 'rgba(65,41,26,0.6)' }">{{ tab.label }}</span>
      </button>
    </div>

    <!-- Контент -->
    <div class="flex-1 overflow-y-auto pt-[72px] pb-24">

      <!-- ══ ИЗБРАННОЕ ══ -->
      <div v-show="activeTab === 'favorites'" class="px-4 pt-3">
        <div v-if="favLoading" class="flex justify-center pt-12">
          <div class="w-5 h-5 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
        </div>

        <template v-else-if="favItems.length > 0">
        <FavTagFilter v-model="favTagFilter" :items="favItems" />
        <p v-if="!favFiltered.length" class="text-13 text-ink/70 text-center pt-6">Нет блюд сразу со всеми выбранными тегами</p>
        <div class="grid grid-cols-2 gap-[6px]">
          <div
            v-for="item in favFiltered"
            :key="item.dish_id"
            class="food-card bg-cream rounded-2xl overflow-hidden relative cursor-pointer"
            @click="openDish(item.dish_id)"
          >
            <div class="relative w-full" style="aspect-ratio: 1/1.1;">
              <img v-if="item.photo_url" :src="imgUrl(item.photo_url)" alt="" class="food-photo w-full h-full object-cover" />
              <div v-if="item.photo_url" class="food-vignette" />
              <div v-else class="w-full h-full bg-[#F0EBE2] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C4B8A8" stroke-width="1.5" stroke-linecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </div>
              <div class="absolute top-1.5 left-1.5 flex flex-col gap-0.5 items-start">
                <span class="pill-kcal text-10 font-semibold">{{ Math.round(item.total_kcal) }}</span>
                <span class="pill-protein text-10">{{ Math.round(item.total_protein) }}</span>
                <span class="pill-fat text-10">{{ Math.round(item.total_fat) }}</span>
                <span class="pill-carbs text-10">{{ Math.round(item.total_carbs) }}</span>
                <!-- Клетчатка есть не у всех: сервер отдаёт null, когда её не
                     разрешил ни один ингредиент. Ноль там был бы неправдой,
                     поэтому пилюли просто нет. -->
                <span v-if="item.total_fiber != null" class="pill-fiber text-10">{{ Math.round(item.total_fiber) }}</span>
              </div>
              <!-- Иконка рецепта -->
              <div
                v-if="item.is_edited_cooking || (item.cooked_weight_g && Number(item.cooked_weight_g) > 0)"
                class="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center"
              >
                <svg width="16" height="14" viewBox="0 0 14 12" fill="none" stroke="#FAF3E8" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M7 2C8.72391 2 10.3772 2.68482 11.5962 3.90381C12.8152 5.12279 13.5 6.77609 13.5 8.5C13.5 8.76522 13.3946 9.0196 13.2071 9.2071C13.0196 9.3946 12.7652 9.5 12.5 9.5H1.5C1.23478 9.5 0.98043 9.3946 0.792893 9.2071C0.605357 9.0196 0.5 8.76522 0.5 8.5C0.5 6.77609 1.18482 5.12279 2.40381 3.90381C3.62279 2.68482 5.27609 2 7 2Z"/>
                  <path d="M7 2V0.5"/>
                  <path d="M0.5 11.5H13.5"/>
                </svg>
              </div>
            </div>
            <div class="px-2.5 py-2 bg-white">
              <p class="text-12 font-semibold text-ink leading-tight line-clamp-2" style="min-height: 2.4em;">{{ item.label }}</p>
            </div>
            <!-- Сердечко -->
            <button
              @click.stop="toggleFavorite(item)"
              class="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
            >
              <svg width="16" height="14" viewBox="0 0 24 20" fill="#FF6B6B" stroke="#FF6B6B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M13.8131 18.2326C12.5923 19.442 10.7015 19.442 9.48071 18.2326L2.19973 11.019C-2.67113 6.14805 3.68934 -4.6136 11.6469 3.34396C19.5905 -4.59961 25.951 6.16221 21.0941 11.019L13.8131 18.2326Z"/>
              </svg>
            </button>
          </div>
        </div>
        </template>

        <ZoyaBubble v-else class="empty-state-bubble">
            <p>{{ ty ? 'Добавь' : 'Добавьте' }} первое блюдо в Избранное,<br>нажав на сердечко на фото любого блюда</p>
          </ZoyaBubble>
      </div>

      <!-- ══ МОИ РЕЦЕПТЫ ══ -->
      <div v-show="activeTab === 'recipes'" class="px-4 pt-3">
        <div v-if="recipesLoading" class="flex justify-center pt-12">
          <div class="w-5 h-5 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
        </div>

        <div v-else-if="recipesItems.length > 0" class="grid grid-cols-2 gap-[6px]">
          <div
            v-for="item in recipesItems"
            :key="item.dish_id"
            class="food-card bg-cream rounded-2xl overflow-hidden relative cursor-pointer"
            @click="openDish(item.dish_id)"
          >
            <div class="relative w-full" style="aspect-ratio: 1/1.1;">
              <img v-if="item.photo_url" :src="imgUrl(item.photo_url)" alt="" class="food-photo w-full h-full object-cover" />
              <div v-if="item.photo_url" class="food-vignette" />
              <div v-else class="w-full h-full bg-[#F0EBE2] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C4B8A8" stroke-width="1.5" stroke-linecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </div>
              <div class="absolute top-1.5 left-1.5 flex flex-col gap-0.5 items-start">
                <span class="pill-kcal text-10 font-semibold">{{ Math.round(item.total_kcal) }}</span>
                <span class="pill-protein text-10">{{ Math.round(item.total_protein) }}</span>
                <span class="pill-fat text-10">{{ Math.round(item.total_fat) }}</span>
                <span class="pill-carbs text-10">{{ Math.round(item.total_carbs) }}</span>
                <!-- Клетчатка есть не у всех: сервер отдаёт null, когда её не
                     разрешил ни один ингредиент. Ноль там был бы неправдой,
                     поэтому пилюли просто нет. -->
                <span v-if="item.total_fiber != null" class="pill-fiber text-10">{{ Math.round(item.total_fiber) }}</span>
              </div>
              <!-- Иконка рецепта -->
              <div
                v-if="item.is_edited_cooking || (item.cooked_weight_g && Number(item.cooked_weight_g) > 0)"
                class="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center"
              >
                <svg width="16" height="14" viewBox="0 0 14 12" fill="none" stroke="#FAF3E8" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M7 2C8.72391 2 10.3772 2.68482 11.5962 3.90381C12.8152 5.12279 13.5 6.77609 13.5 8.5C13.5 8.76522 13.3946 9.0196 13.2071 9.2071C13.0196 9.3946 12.7652 9.5 12.5 9.5H1.5C1.23478 9.5 0.98043 9.3946 0.792893 9.2071C0.605357 9.0196 0.5 8.76522 0.5 8.5C0.5 6.77609 1.18482 5.12279 2.40381 3.90381C3.62279 2.68482 5.27609 2 7 2Z"/>
                  <path d="M7 2V0.5"/>
                  <path d="M0.5 11.5H13.5"/>
                </svg>
              </div>
            </div>
            <div class="px-2.5 py-2 bg-white">
              <p class="text-12 font-semibold text-ink leading-tight line-clamp-2" style="min-height: 2.4em;">{{ item.label }}</p>
            </div>
            <!-- Сердечко -->
            <button
              @click.stop="toggleFavorite(item)"
              class="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/20 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
            >
              <svg width="16" height="14" viewBox="0 0 24 20"
                :fill="item.is_favorite ? '#FF6B6B' : 'none'"
                :stroke="item.is_favorite ? '#FF6B6B' : 'white'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M13.8131 18.2326C12.5923 19.442 10.7015 19.442 9.48071 18.2326L2.19973 11.019C-2.67113 6.14805 3.68934 -4.6136 11.6469 3.34396C19.5905 -4.59961 25.951 6.16221 21.0941 11.019L13.8131 18.2326Z"/>
              </svg>
            </button>
          </div>
        </div>

        <ZoyaBubble v-else class="empty-state-bubble">
            <p>2 способа создать рецепт:<br><br>
            1. {{ ty ? 'Отредактируй' : 'Отредактируйте' }} блюдо<br>из Избранного и {{ ty ? 'сохрани' : 'сохраните' }}<br>как рецепт.<br>
            2. {{ ty ? 'Создай' : 'Создайте' }} новый рецепт кнопкой «+».</p>
          </ZoyaBubble>
      </div>

    </div>

    <!-- FAB: создать новый рецепт (только на вкладке «Рецепты»). Как «+» в
         дневнике: стекло .glass-fab, 90 px, та же иконка и место экрана
         (.fab-recipe). -->
    <button
      v-show="activeTab === 'recipes'"
      data-guide="recipe-fab"
      @click="createNewRecipe"
      aria-label="Новый рецепт"
      class="fab-recipe fixed flex items-center justify-center rounded-full glass-fab active:scale-95 transition-transform z-40 select-none"
    >
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FAF3E8" stroke-width="1.5" stroke-linecap="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import { imgUrl } from '../utils/image.js'
import FavTagFilter, { matchesTags } from '../components/FavTagFilter.vue'
import ZoyaBubble from '../components/ZoyaBubble.vue'

const router = useRouter()
const authStore = useAuthStore()
const ty = computed(() => authStore.user?.address_form !== 'вы')

const tabs = [
  {
    key: 'favorites',
    label: 'Избранное',
    svg: '<svg width="20" height="17" viewBox="0 0 24 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M13.8131 18.2326C12.5923 19.442 10.7015 19.442 9.48071 18.2326L2.19973 11.019C-2.67113 6.14805 3.68934 -4.6136 11.6469 3.34396C19.5905 -4.59961 25.951 6.16221 21.0941 11.019L13.8131 18.2326Z"/></svg>',
  },
  {
    key: 'recipes',
    label: 'Мои рецепты',
    svg: '<svg width="20" height="17" viewBox="0 0 14 12" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M7 2C8.72391 2 10.3772 2.68482 11.5962 3.90381C12.8152 5.12279 13.5 6.77609 13.5 8.5C13.5 8.76522 13.3946 9.0196 13.2071 9.2071C13.0196 9.3946 12.7652 9.5 12.5 9.5H1.5C1.23478 9.5 0.98043 9.3946 0.792893 9.2071C0.605357 9.0196 0.5 8.76522 0.5 8.5C0.5 6.77609 1.18482 5.12279 2.40381 3.90381C3.62279 2.68482 5.27609 2 7 2Z"/><path d="M7 2V0.5"/><path d="M0.5 11.5H13.5"/></svg>',
  },
]

// По умолчанию — «Мои рецепты»; выбранная вкладка помнится на время
// сессии, чтобы «назад» со страницы блюда возвращал на ту же вкладку,
// а не всегда на рецепты.
const TAB_KEY = 'zoya-cooking-tab'
function savedTab() {
  try { return sessionStorage.getItem(TAB_KEY) === 'favorites' ? 'favorites' : 'recipes' } catch { return 'recipes' }
}
const activeTab = ref(savedTab())
watch(activeTab, (tab) => { try { sessionStorage.setItem(TAB_KEY, tab) } catch { /* приватный режим */ } })

// Избранное
const favItems = ref([])
const favLoading = ref(false)
// Фильтр по тегам (FavTagFilter)
const favTagFilter = ref([])
const favFiltered = computed(() => favItems.value.filter(it => matchesTags(it, favTagFilter.value)))

async function loadFavorites() {
  favLoading.value = true
  try {
    const { data } = await api.get('/cooking/favorites')
    favItems.value = data.items || []
  } catch { /* silent */ }
  finally { favLoading.value = false }
}

// Мои рецепты
const recipesItems = ref([])
const recipesLoading = ref(false)

async function loadRecipes() {
  recipesLoading.value = true
  try {
    const { data } = await api.get('/cooking/my-recipes')
    recipesItems.value = data.items || []
  } catch { /* silent */ }
  finally { recipesLoading.value = false }
}

function openDish(dishId) {
  router.push(`/cooking/dish/${dishId}`)
}

async function createNewRecipe() {
  try {
    const { data } = await api.post('/diary/dishes', {
      name: 'Новый рецепт',
      is_edited_cooking: true,
    })
    if (data.dish?.id) router.push(`/cooking/dish/${data.dish.id}`)
  } catch (err) {
    console.error('Create recipe error:', err)
  }
}

async function toggleFavorite(item) {
  try {
    const { data } = await api.patch(`/diary/dishes/${item.dish_id}/favorite`)
    item.is_favorite = data.is_favorite
    // Если убрали из избранного на вкладке "Избранное" — удаляем из списка
    if (!data.is_favorite && activeTab.value === 'favorites') {
      favItems.value = favItems.value.filter(f => f.dish_id !== item.dish_id)
    }
  } catch (err) {
    console.error('Toggle favorite error:', err)
  }
}

watch(activeTab, (tab) => {
  if (tab === 'recipes' && recipesItems.value.length === 0 && !recipesLoading.value) loadRecipes()
  if (tab === 'favorites' && favItems.value.length === 0 && !favLoading.value) loadFavorites()
})

onMounted(() => {
  if (activeTab.value === 'favorites') loadFavorites()
  else loadRecipes()
})
</script>

<style scoped>
.empty-state-bubble { margin: 28px 24px 36px; flex-shrink: 0; }
.empty-state-bubble p { margin: 0; }
.pill-kcal    { background: #EE998C; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-protein { background: #9FC2C1; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fat     { background: #EBC06D; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-carbs   { background: #EEAD77; border-radius: 8px; padding: 2px 8px; color: var(--ink); }
.pill-fiber   { background: #A6B779; border-radius: 8px; padding: 2px 8px; color: var(--ink); }

/* Перевёрнутые скругления у активного таба */
.tab-active::before,
.tab-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 14px;
  height: 14px;
  background: rgba(250, 243, 232, 0.75);
}
.tab-active::before {
  left: -14px;
  -webkit-mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
}
.tab-active::after {
  right: -14px;
  -webkit-mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
}
.tab-active.tab-first::before { display: none; }
.tab-active.tab-last::after { display: none; }

/* Место кнопки «Новый рецепт» — как у «+» в дневнике (Diary.vue, .fab-add):
 * в 20 px от правого края колонки 430 px, над нижним меню */
.fab-recipe {
  width: 90px;
  height: 90px;
  bottom: max(calc(98px + var(--sheet-bottom, 0px)), calc(72px + 12px + var(--nav-inset)));
  right: 20px;
}
@media (min-width: 430px) {
  .fab-recipe { right: calc(50% - 215px + 20px); }
}
</style>
